import { Form, Input, Select, type FormInstance } from "antd";
import { useEffect } from "react";
import type { FieldConfig, SourcingNodeConfig } from "../../../type";
import { MaterialFileFormFields } from "@/components/commonFormItems/MaterialFileFormFields";
import SelectSupplierModal from "@/components/supplier/SelectSupplierModal";
import AdmissionAssessmentSelectModal from "@/components/supplier/AdmissionAssessmentSelectModal";
import type { SupplierUserSimpleListItem } from "@/types/apis";
import { useSupplierSelection } from "./hooks/useSupplierSelection";
import { useAdmissionAssessmentSelect } from "./hooks/useAdmissionAssessmentSelect";
import { DeleteOutlined } from "@ant-design/icons";
import AdmissionApplicationSelectModal from "@/components/supplier/AdmissionApplicationSelectModal";
import { useAdmissionApplicationSelect } from "./hooks/useAdmissionApplicationSelect";

// 供应商选择字段组件
// 只读 Select，点击打开供应商选择弹窗，支持取消选择
const SupplierSelectField: React.FC<{
  field: { name: string; label: string; required?: boolean };
  selectedSuppliers: SupplierUserSimpleListItem[];
  onOpenModal: () => void;
  onRemove: (id: string) => void;
  disabled: boolean;
}> = ({ field, selectedSuppliers, onOpenModal, onRemove, disabled }) => (
  <Form.Item
    name={field.name}
    label={field.label}
    rules={field.required ? [{ required: true }] : []}
  >
    <Select
      mode="multiple"
      open={false}
      onClick={disabled ? void 0 : onOpenModal}
      onDeselect={disabled ? void 0 : (value) => onRemove(value)}
      placeholder="点击选择供应商"
      options={selectedSuppliers.map((s) => ({
        value: s.id,
        label: s.supplierName,
      }))}
    />
  </Form.Item>
);

const AdmissionSelectField: React.FC<{
  field: { name: string; label: string; required?: boolean };
  fieldValue: string;
  onOpenModal: () => void;
  onRemove: () => void;
  disabled: boolean;
  placeholder?: string;
}> = ({ field, fieldValue, onOpenModal, onRemove, disabled, placeholder }) => {
  // 有值时显示 span + 移除按钮
  if (fieldValue) {
    return (
      <Form.Item name={field.name} label={field.label}>
        <span>{fieldValue}</span>
        {!disabled && (
          <DeleteOutlined
            onClick={onRemove}
            style={{ marginLeft: 8, cursor: "pointer", color: "red" }}
          />
        )}
      </Form.Item>
    );
  }
  // 无值时显示 Input
  return (
    <Form.Item
      name={field.name}
      label={field.label}
      rules={field.required ? [{ required: true }] : []}
    >
      <Input
        placeholder={placeholder}
        onClick={disabled ? void 0 : onOpenModal}
        readOnly
      />
    </Form.Item>
  );
};

// 表单项渲染器
// 根据字段类型渲染对应的表单项组件
const FieldRenderer: React.FC<{
  field: FieldConfig;
  form: FormInstance;
  selectedSuppliers?: SupplierUserSimpleListItem[];
  onOpenSupplierModal: () => void;
  onOpenAdmissionAssessmentModal: () => void;
  onOpenAdmissionApplicationModal: () => void;
  onRemoveSupplier?: (id: string) => void;
  onRemoveAdmissionAssessment?: () => void;
  onRemoveAdmissionApplication?: () => void;
  disabled: boolean;
}> = (props) => {
  const { field, disabled, form } = props;

  switch (field.type) {
    case "input":
      return (
        <Form.Item
          name={field.name}
          label={field.label}
          rules={field.required ? [{ required: true }] : []}
        >
          <Input placeholder={field.placeholder} />
        </Form.Item>
      );

    case "upload":
      return (
        <MaterialFileFormFields
          required={field.required}
          label={field.label}
          fieldName={field.name}
          hiddenRemove={disabled}
        />
      );

    case "supplierSelect":
      return (
        <SupplierSelectField
          field={field}
          selectedSuppliers={props.selectedSuppliers || []}
          onOpenModal={props.onOpenSupplierModal}
          onRemove={props.onRemoveSupplier || (() => {})}
          disabled={disabled}
        />
      );
    case "admissionAssessment":
      return (
        <AdmissionSelectField
          field={field}
          fieldValue={form.getFieldValue(field.name)}
          onOpenModal={props.onOpenAdmissionAssessmentModal}
          disabled={disabled}
          onRemove={props.onRemoveAdmissionAssessment || (() => {})}
          placeholder="请选择准入评估"
        />
      );
    case "admissionApplication":
      return (
        <AdmissionSelectField
          field={field}
          fieldValue={form.getFieldValue(field.name)}
          onOpenModal={props.onOpenAdmissionApplicationModal}
          disabled={disabled}
          onRemove={props.onRemoveAdmissionApplication || (() => {})}
          placeholder="请选择准入申请"
        />
      );
  }
};

// 寻源节点表单组件
// 负责渲染节点配置的字段列表，管理供应商选择弹窗
export const NodeForm: React.FC<{
  disabled: boolean;
  node: SourcingNodeConfig;
  form: FormInstance;
}> = ({ node, form, disabled }) => {
  const {
    modalOpen,
    openModal,
    addSuppliers,
    removeSupplier,
    getSuppliers,
    initSuppliersMap,
    currentSupplierStage,
    closeModal,
  } = useSupplierSelection(form);

  const {
    admissionAssessmentOpen,
    openAdmissionAssessmentModal,
    closeAdmissionAssessmentModal,
    addAdmissionAssessment,
    removeAdmissionAssessment,
  } = useAdmissionAssessmentSelect(form);

  const {
    admissionApplicationOpen,
    openAdmissionApplicationModal,
    closeAdmissionApplicationModal,
    addAdmissionApplication,
    removeAdmissionApplication,
  } = useAdmissionApplicationSelect(form);

  // 初始化供应商列表（用于回显）
  // 当 node.extra.supplierList 存在时，为所有 supplierSelect 类型字段初始化供应商数据
  useEffect(() => {
    if (!node.extra?.supplierList) return;
    node.fields
      .filter(
        (f): f is Extract<FieldConfig, { type: "supplierSelect" }> =>
          f.type === "supplierSelect",
      )
      .forEach((field) => {
        initSuppliersMap(node.extra!.supplierList!, field.name);
      });
  }, [node.extra, node.fields, initSuppliersMap]);

  return (
    <>
      {/* 使用 component={false} 避免渲染真实 <form> 标签，防止嵌套警告 */}
      <Form form={form} component={false} disabled={disabled}>
        {node.fields.map((f) => (
          <FieldRenderer
            key={f.name}
            field={f}
            form={form}
            disabled={disabled}
            selectedSuppliers={getSuppliers(f.name)}
            onOpenSupplierModal={() =>
              openModal(f.name, "supplierStage" in f ? f.supplierStage : void 0)
            }
            onRemoveSupplier={(id) => removeSupplier(f.name, id)}
            onOpenAdmissionAssessmentModal={openAdmissionAssessmentModal}
            onRemoveAdmissionAssessment={() =>
              removeAdmissionAssessment(f.name)
            }
            onOpenAdmissionApplicationModal={openAdmissionApplicationModal}
            onRemoveAdmissionApplication={() => {
              console.log(f.name);
              removeAdmissionApplication(f.name);
            }}
          />
        ))}
      </Form>

      <SelectSupplierModal
        open={modalOpen}
        multiple
        fixedSupplierStage={currentSupplierStage}
        onOkMulti={(suppliers) => {
          if (suppliers) addSuppliers(suppliers);
          closeModal();
        }}
      />

      <AdmissionAssessmentSelectModal
        open={admissionAssessmentOpen}
        onClose={closeAdmissionAssessmentModal}
        onOk={(record) => {
          if (record.length) {
            addAdmissionAssessment("relatedForm", record[0]);
          }
        }}
      />

      <AdmissionApplicationSelectModal
        open={admissionApplicationOpen}
        onClose={closeAdmissionApplicationModal}
        onOk={(record) => {
          if (record.length) {
            addAdmissionApplication("relatedForm", record[0]);
          }
        }}
      />
    </>
  );
};
